import "./index.less";
import { Button, Drawer, Dropdown } from "antd";
import { Header } from "antd/es/layout/layout";
import { MenuOutlined, CloseOutlined, DownOutlined, RightOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useScreenSize } from "~/hooks/useScreenSize";
import { useLanguageConfig } from "~/i18n/useLanguageConfig";
import { useSystemList } from "~/hooks/system/useSystemList";
import i18n from "~/i18n";
import vi from "~/assets/logo/vi.webp";
import en from "~/assets/logo/en.webp";
import logoImg from "~/assets/logo/logo-no-bg.png";
import { useThemeStore } from "~/stores/themeStore";

const SYSTEMS = [
  { key: 'erpMini', title: 'ERP Mini', tag: 'ERP', path: '/erpMini' },
  { key: 'platformAI', title: 'AI B2B Platform', tag: 'AI', path: 'https://platform.apetechs.co/', isExternal: true },
  { key: 'crm', title: 'CRM', tag: 'Sales', path: '/crm' },
  { key: 'dms', title: 'DMS', tag: 'Sales', path: '/dms' },
  { key: 'pm', title: 'PM', tag: 'Project', path: '/pm' },
  { key: 'hrm', title: 'HRM', tag: 'HR', path: '/hrm' },
  { key: 'pms', title: 'PMS', tag: 'Procurement', path: '/pms' },
  { key: 'mes', title: 'MES', tag: 'Production', path: '/mes' },
  { key: 'tms', title: 'TMS', tag: 'Logistics', path: '/tms' },
  { key: 'wms', title: 'WMS', tag: 'Warehouse', path: '/wms' },
  { key: 'eam', title: 'EAM', tag: 'Asset', path: '/eam' },
  { key: 'apexLink', title: 'APExLINK', tag: 'Blockchain', path: 'https://apexlink.dev/', isExternal: true },
];

const MainHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { themeStyle, toggleTheme } = useThemeStore() as any;
  // const { data: dataSystem }        = useSystemList();
  const { isMobile, isTablet } = useScreenSize();
  const { currentLanguageConfig, languageList } = useLanguageConfig();
  const { t: tNav } = useTranslation('nav');
  const { t: tHome } = useTranslation('home');
  const { t: tCommon } = useTranslation('common');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [documentIds, setDocumentIds] = useState<Record<string, string>>({});

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // useEffect(() => {
  //   if (!dataSystem?.data) return;
  //   const map: Record<string, string> = {};
  //   dataSystem.data.forEach((sys: any) => {
  //     if (sys.key && sys.documentId) map[sys.key] = sys.documentId;
  //   });
  //   setDocumentIds(map);
  // }, [dataSystem]);

  const navigateToSystem = (item: typeof SYSTEMS[0]) => {
    if (item.isExternal) { window.open(item.path, "_blank"); return; }
    const docId = documentIds[item.key];
    if (docId) { window.open(`/system/${item.key}/${docId}`, "_blank"); return; }
    navigate(item.path);
  };

  const toggleLanguage = () => {
    const idx = languageList.indexOf(i18n.language);
    i18n.changeLanguage(languageList[(idx + 1) % languageList.length]);
  };

  const isActive = (path: string) => location.pathname === path;

  const isVietnamese = currentLanguageConfig.label === "Vietnamese";
  const langImg = isVietnamese ? vi : en;
  const langLabel = isVietnamese ? "VI" : "EN";

  const NAV_LINKS = [
    { label: tNav('news'), path: '/blog' },
    { label: tNav('contact'), path: '/contact' },
    { label: tNav('policy'), path: '/privacy-policy' },
  ];

  /* ── Products mega-menu ────────────────────────── */
  const ProductsMegaMenu = (
    <div className="hdr-mega-menu">
      <div className="hdr-mega-header">
        <div>
          <div className="hdr-mega-title">{tNav('ecosystem_title')}</div>
          <div className="hdr-mega-sub">{tNav('ecosystem_count')}</div>
        </div>
        <span
          className="hdr-mega-viewall"
          onClick={() => navigate("/")}
        >
          {tNav('view_all')}
        </span>
      </div>
      <div className="hdr-mega-grid">
        {SYSTEMS.map((sys) => (
          <div
            key={sys.key}
            className="hdr-mega-item"
            onClick={() => navigateToSystem(sys)}
          >
            <div className="hdr-mega-item-top">
              <span className="hdr-mega-tag">{sys.tag}</span>
              {sys.isExternal && <span className="hdr-mega-ext">↗</span>}
            </div>
            <div className="hdr-mega-item-name">{sys.title}</div>
            <div className="hdr-mega-item-desc">{tHome(`systems.${sys.key}.desc`)}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={`main-header${scrolled ? " scrolled" : ""}`}>
      <Header style={{ background: "transparent", height: 56, padding: 0 }}>
        <div className="header-inner">

          {/* ── LOGO ─────────────────────────────── */}
          <div className="header-logo" onClick={() => navigate("/")}>
            <img src={logoImg} alt="Apetechs" className="header-logo-img" />
            <span className="header-logo-prefix">&gt;_</span>
            <span className="header-logo-text">APETECHS</span>
            <span className="header-logo-cursor" />
          </div>

          {/* ── DESKTOP NAV ──────────────────────── */}
          {!isMobile && !isTablet && (
            <nav className="header-nav">
              {/* Sản phẩm — dropdown */}
              <Dropdown
                dropdownRender={() => ProductsMegaMenu}
                trigger={["hover"]}
                placement="bottomLeft"
                overlayClassName="hdr-mega-overlay"
              >
                <span className="header-nav-link header-nav-products">
                  {tNav('products')}
                  <DownOutlined className="header-nav-arrow" />
                </span>
              </Dropdown>

              {/* Standard nav links */}
              {NAV_LINKS.map((link) => (
                <span
                  key={link.path}
                  className={`header-nav-link${isActive(link.path) ? " active" : ""}`}
                  onClick={() => navigate(link.path)}
                >
                  {link.label}
                </span>
              ))}
            </nav>
          )}

          {/* ── RIGHT ACTIONS ────────────────────── */}
          <div className="header-actions">
            {/* Theme toggle */}
            <button
              className="header-theme-toggle"
              onClick={toggleTheme}
              title={themeStyle === 'dark' ? tCommon('theme.to_light') : tCommon('theme.to_dark')}
              aria-label="Toggle theme"
            >
              {themeStyle === 'dark' ? '☀' : '☾'}
            </button>

            {!isMobile && !isTablet && (
              <>
                <div className="header-lang-btn" onClick={toggleLanguage} title={currentLanguageConfig.label}>
                  <img src={langImg} alt={langLabel} />
                  <span>{langLabel}</span>
                </div>
                <button className="header-cta-btn" onClick={() => navigate("/contact")}>
                  {tNav('cta')}
                </button>
              </>
            )}

            {(isMobile || isTablet) && (
              <Button
                className="mobile-menu-button"
                icon={<MenuOutlined />}
                onClick={() => setMobileMenuOpen(true)}
              />
            )}
          </div>
        </div>
      </Header>

      {/* ── MOBILE DRAWER ───────────────────────── */}
      <Drawer
        title={null}
        placement="right"
        onClose={() => { setMobileMenuOpen(false); setMobileProductsOpen(false); }}
        open={mobileMenuOpen}
        className="mobile-menu-drawer"
        width="85%"
      >
        {/* Drawer header */}
        <div className="mobile-drawer-header">
          <div className="mobile-drawer-logo-text">
            <img src={logoImg} alt="Apetechs" style={{ height: 30, marginRight: 8, verticalAlign: "middle" }} />
            <span>APETECHS</span>
          </div>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={() => { setMobileMenuOpen(false); setMobileProductsOpen(false); }}
            className="mobile-drawer-close-btn"
          />
        </div>

        <div className="mobile-menu-content">
          {/* Sản phẩm — expandable */}
          <div
            className="mobile-menu-item mobile-menu-item-parent"
            onClick={() => setMobileProductsOpen((v) => !v)}
          >
            <span>{tNav('products')}</span>
            <RightOutlined
              style={{
                fontSize: 11,
                transition: "transform 0.2s",
                transform: mobileProductsOpen ? "rotate(90deg)" : "rotate(0deg)",
              }}
            />
          </div>

          {mobileProductsOpen && (
            <div className="mobile-products-list">
              {SYSTEMS.map((sys) => (
                <div
                  key={sys.key}
                  className="mobile-products-item"
                  onClick={() => { navigateToSystem(sys); setMobileMenuOpen(false); setMobileProductsOpen(false); }}
                >
                  <span className="mobile-products-tag">{sys.tag}</span>
                  <span className="mobile-products-name">{sys.title}</span>
                  {sys.isExternal && <span style={{ color: "#FBAD16", fontSize: 11, marginLeft: 4 }}>↗</span>}
                </div>
              ))}
            </div>
          )}

          {/* Standard nav links */}
          {NAV_LINKS.map((link) => (
            <div
              key={link.path}
              className={`mobile-menu-item${isActive(link.path) ? " active" : ""}`}
              onClick={() => { navigate(link.path); setMobileMenuOpen(false); }}
            >
              {link.label}
            </div>
          ))}

          {/* Bottom actions */}
          <div className="mobile-drawer-footer">
            <div className="header-lang-btn" onClick={toggleLanguage}>
              <img src={langImg} alt={langLabel} />
              <span>{langLabel}</span>
            </div>
            <button
              className="header-cta-btn"
              onClick={() => { navigate("/contact"); setMobileMenuOpen(false); }}
            >
              {tNav('cta')}
            </button>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default MainHeader;
